import { FC, useCallback, useState } from 'react'
import { useDispatch } from 'hooks/useDispatch'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'rui/widgets/ExternalLink'
import { signUp } from 'actions/auth'

import Page from 'rui/widgets/Page'
import useCandidateToken from 'hooks/useCandidateToken'

import HiddenUnlessFieldError from 'rui/widgets/HiddenUnlessFieldError'

import PasswordRequirements from 'rui/widgets/PasswordRequirements'

import ErrorBanner from 'rui/widgets/FormBaseErrorFlashBanner'

import { Form, Container, Spacer, Text, Loader, FormSerialization } from 'roadie-ui'
import { capitalize } from 'util/format'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

type SubmitError = {
  response: {
    status: number | string
    data?: {
      errors?: {
        [key: string]: string | string[] | string[][]
      }
    }
  }
}

const { validations } = Form

const DriveSignUp: FC = () => {
  const { t } = useLocalization()
  const [submitErrorCode, setSubmitErrorCode] = useState<number | string>()

  const dispatch = useDispatch()

  // @ts-expect-error // needs types for redux state
  const mediaSize = useSelector((state) => state.config.mediaSize)
  const isMobile = mediaSize === 'mobile'

  const candidate = useCandidateToken()

  const handleData = useCallback(
    async (data: FormSerialization) => {
      if (!data.marketing_comm_opt_in) {
        data.marketing_comm_opt_in = 'false'
      }

      try {
        await dispatch(signUp(data))
      } catch (e) {
        const err = e as SubmitError
        if (Number(err?.response?.status) === 407) {
          throw {
            ...err,
            response: { ...err.response, data: { errors: { base: t('dweb_sign_up_turn_off_vpn') } } },
          }
        }
        if (err?.response?.data?.errors) {
          // Capitalize the errors that are returned from main api
          const errors = err.response.data.errors
          const capitalizedErrors: Record<string, any> = {}
          for (const key in errors) {
            let value = errors[key]
            // Flatten nested arrays and join messages
            if (Array.isArray(value)) {
              value = value.flat(Infinity).join(', ')
            }
            capitalizedErrors[key] = capitalize(value)
          }
          throw { ...err, response: { ...err.response, data: { ...err.response.data, errors: capitalizedErrors } } }
        }
        setSubmitErrorCode(err.response.status)
        throw err
      }
    },
    [dispatch, t]
  )

  if (candidate.loading) {
    return <Loader />
  }

  return (
    <>
      <Page>
        <Container.Grid $gridTemplateColumns={isMobile ? '100%' : '48rem'} $justifyContent="center">
          <Container.Panel $textAlign="center">
            <Text.H4 $textAlign="center" $color="neutral-black">
              <LocalizedText translationKey="dweb_rui_drive_sign_up_driver_sign_up" />
            </Text.H4>
            <Spacer $height="1.6rem" />
            <Text.Body1 $color="neutral-black">
              <LocalizedRichText
                translationKey="dweb_rui_drive_sign_up_learn_more_about_roadie_link1"
                params={{
                  link1: (
                    <ExternalLink href="http://driver.roadie.com">
                      <Text.Body1 $fontWeight="700" baseElement="span">
                        <LocalizedText translationKey="dweb_common_here" />
                      </Text.Body1>
                    </ExternalLink>
                  ),
                }}
              />
            </Text.Body1>
            <Form.Basic
              id="sign-up-form"
              onSubmit={handleData}
              validations={[
                ['first_name', validations.required],
                ['last_name', validations.required],
                ['email', [validations.email, validations.required]],
                ['phone_number', [validations.phone, validations.required]],
                ['password', [validations.required, validations.password], "Doesn't meet requirements"],
              ]}
            >
              <ErrorBanner />
              <HiddenUnlessFieldError name="first_name" value={candidate.data.first_name}>
                <Form.Input
                  type="name"
                  name="first_name"
                  label={t('dweb_account_basic_info_registration_first_name')}
                  defaultValue={candidate.data.first_name}
                />
              </HiddenUnlessFieldError>
              <HiddenUnlessFieldError name="last_name" value={candidate.data.last_name}>
                <Form.Input
                  type="name"
                  name="last_name"
                  label={t('dweb_account_basic_info_registration_last_name')}
                  defaultValue={candidate.data.last_name}
                />
              </HiddenUnlessFieldError>
              <HiddenUnlessFieldError name="email" value={candidate.data.email}>
                <Form.Input
                  type="email"
                  name="email"
                  label={t('dweb_forgot_password_email_address')}
                  defaultValue={candidate.data.email}
                />
              </HiddenUnlessFieldError>
              <HiddenUnlessFieldError name="password" value={candidate.data.password}>
                <Form.Input
                  type="password"
                  name="password"
                  label={t('dweb_common_password')}
                  defaultValue={candidate.data.password}
                >
                  <PasswordRequirements />
                </Form.Input>
              </HiddenUnlessFieldError>
              <HiddenUnlessFieldError name="phone_number" value={candidate.data.phone_number}>
                <Form.Input
                  type="phone"
                  name="phone_number"
                  label={t('dweb_account_email_and_phone_mobile_number')}
                  defaultValue={candidate.data.phone_number}
                />
              </HiddenUnlessFieldError>

              <Spacer $height="0.8rem" />
              <Text.Body2 $textAlign="left" $color="neutral-black">
                <LocalizedText translationKey="dweb_rui_drive_sign_up_well_send_verification_code_via_sms_confirm_mobile" />
              </Text.Body2>
              <input type="hidden" name="basic_user_propensity" value="driver" />
              <Spacer $height="2.4rem" />

              <Container.Grid $gridTemplateColumns="2.8rem 1fr" $alignItems="flex-start">
                <Form.Checkbox id="marketing_comm_opt_in" name="marketing_comm_opt_in" value="true" label="" />
                <label htmlFor="marketing_comm_opt_in">
                  <Text.Caption $textAlign="left">
                    <LocalizedRichText
                      translationKey="dweb_rui_drive_sign_up_i_consent_receiving_automated_texts_at_mobile_number"
                      params={{
                        link1: (
                          <ExternalLink href="https://www.roadie.com/privacy">
                            <Text.Caption $fontWeight="700" baseElement="span">
                              <LocalizedText translationKey="dweb_verify_identity_privacy_policy" />
                            </Text.Caption>
                          </ExternalLink>
                        ),
                        link2: (
                          <ExternalLink href="https://www.roadie.com/terms">
                            <Text.Caption $fontWeight="700" baseElement="span">
                              <LocalizedText translationKey="dweb_terms_terms_conditions" />
                            </Text.Caption>
                          </ExternalLink>
                        ),
                      }}
                    />
                  </Text.Caption>
                </label>
              </Container.Grid>

              <Spacer $height="1.6rem" />
              <Form.Submit>
                <LocalizedText translationKey="dweb_sign_in_sign_up" />
              </Form.Submit>
              <Spacer $height="2.4rem" />
              <Text.Body1 $color="neutral-black">
                <LocalizedRichText
                  translationKey="dweb_rui_drive_sign_up_have_account_link1"
                  params={{
                    link1: (
                      <Link to="sign-in">
                        <LocalizedText translationKey="dweb_common_sign_in" />
                      </Link>
                    ),
                  }}
                />
              </Text.Body1>
              {submitErrorCode && <Spacer $height="1.6rem" />}
              {submitErrorCode && (
                <Text.Body2>
                  <LocalizedRichText
                    translationKey="dweb_sign_in_having_trouble_link1"
                    params={{
                      link1: (
                        <ExternalLink href="https://support.roadie.com/support/s/contactsupport">
                          <LocalizedText translationKey="dweb_sign_in_contact_us" />
                        </ExternalLink>
                      ),
                    }}
                  />
                </Text.Body2>
              )}
            </Form.Basic>
          </Container.Panel>
        </Container.Grid>
      </Page>
    </>
  )
}

export default DriveSignUp
